import { api } from '@/lib/api';
import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';
import { queryKeys } from '../queryKeys';

export interface CreateTempCustomerRequest {
  customer_firstname: string;
  customer_lastname: string;
  phone: string;
}

/** Walk-in customer. Backend assigns the `temp{id}` email — not sent by us. */
export interface TempCustomer {
  id: number;
  name: string;
  surname: string;
  phone: string;
}

export interface CreateTempOrderRequest {
  cart_id: number;
}

/**
 * POST admin/temp-customers — create a walk-in/offline customer.
 * No toast/error handling here: consumed via useFormMutation so validation
 * errors map onto the form fields.
 */
export const createTempCustomerMutation = () =>
  mutationOptions({
    mutationKey: mutationKeys.tempOrders.createCustomer(),
    mutationFn: async (values: CreateTempCustomerRequest) => {
      const response = await api
        .post('admin/temp-customers', { json: values })
        .json<{ data: TempCustomer }>();
      return response.data;
    },
  });

/** POST admin/temp-orders — turn a finished cart into a Paid order. */
export const createTempOrderMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.tempOrders.createOrder(),
    mutationFn: async (values: CreateTempOrderRequest) => {
      const response = await api
        .post('admin/temp-orders', { json: values })
        .json<{ data: unknown }>();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.orders.index] });
      toast.success('Paid order created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create order');
    },
  });
};
